import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, AlertTriangle, Shield, HardDrive, BookOpen, PlayCircle, Trash2, BarChart3, ArrowLeft } from 'lucide-react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const APP_STORAGE_KEYS = [
  'emergency_inventory_kits',
  'inventory_household_size',
  'inventory_custom_units',
  'inventory_templates',
  'document_audit_data',
  'document_uploads',
  'document_custom_items',
  'document_custom_sections',
  'document_section_order',
  'document_item_order',
  'document_hidden_items',
  'emergency_plan_data',
  'review_reminder_dismissed',
  'document_audit_pets',
  'map_index_data',
  'admin_token',
];

const ToolHub = () => {
  const [pulsing, setPulsing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setPulsing(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const tools = [
    {
      id: 'emergency',
      title: 'Emergency Contact Sheet Generator',
      description:
        'Build emergency contact sheets you can print for home, travel, or your wallet.',
      icon: '🚨',
      path: '/emergency',
      features: [
        'Starts with 140+ common emergency numbers',
        'Master List, Travel, and Wallet layouts',
        'Drag & drop to reorder',
        'Save and load your work',
        'Ready to print',
      ],
      className: 'emergency',
    },
    {
      id: 'maintenance',
      title: 'Home Maintenance List Generator',
      description:
        'Plan your home maintenance month by month with seasonal tasks and progress tracking.',
      icon: '🏠',
      path: '/maintenance',
      features: [
        'Month-by-month task list',
        'Starts with common home tasks',
        'See your progress at a glance',
        'Seasonal reminders built in',
        'Print your schedule',
      ],
      className: 'maintenance',
    },
    {
      id: 'debt-test',
      title: 'Debt Management Tool',
      description:
        'Find the best way to pay off your debts. Compare payoff strategies and see how much you can save.',
      icon: '🧮',
      path: '/debt-test',
      features: [
        'Full loan payment schedules',
        'Smart scoring to rank your debts',
        'Best payoff order across all debts',
        'Put freed-up money toward the next debt',
        'Set goals and compare "what if" scenarios',
      ],
      className: 'debt-test',
    },
    {
      id: 'budget',
      title: 'Budget Creation Tool',
      description:
        'Build a budget to see how much money you have left after bills. Includes emergency fund planning and expense tracking.',
      icon: '💰',
      path: '/budget',
      features: [
        'Emergency fund calculator ($1000 × family age)',
        'Emergency cash tracking ($500 minimum)',
        'Full list of expense categories',
        'Charts to see where your money goes',
        'See what\'s left over for debt payments',
      ],
      className: 'budget',
    },
    {
      id: 'inventory',
      title: 'Emergency Supply Inventory',
      description:
        'Keep track of your emergency kits and supplies. See everything by kit or all at once, and get alerts when items expire.',
      icon: '📦',
      path: '/inventory',
      features: [
        'Organize by kit with starter templates',
        'View all items across every kit',
        'Expiration alerts so nothing goes bad',
        'Checklists to make sure you\'re covered',
        'Save, load, and print your inventory',
      ],
      className: 'inventory',
    },
    {
      id: 'emergency-plan',
      title: 'Emergency Plan Creator',
      description:
        'Build plans for your family, pets, home, and more. Covers evacuation, communication, medical needs, and grab-and-go bags. Save to add to your binder.',
      icon: '📋',
      path: '/emergency-plan',
      features: [
        'Family info, escape routes, and disaster plans',
        'Communication, medical, home, and pet info',
        'Grab-and-go checklist, school & work plans',
        'Save for your binder',
      ],
      className: 'emergency-plan',
      status: 'testing',
    },
    {
      id: 'documents',
      title: 'Important Documents',
      description:
        'Check off your important documents. Mark each one as physical, digital, or missing — then see what you still need to do.',
      icon: '📄',
      path: '/documents',
      features: [
        '8-section checklist with a to-do list',
        'Mark each item as physical, digital, or missing',
        'Save for binder',
      ],
      className: 'documents',
      status: 'early development',
    },
    {
      id: 'map-index',
      title: 'Map Index',
      description:
        'Free map links with step-by-step instructions. Add your address to include your coordinates in your binder for when you need a map offline.',
      icon: '🗺️',
      path: '/map-index',
      features: [
        'Free maps: OpenTopoMap, OpenRailwayMap, USGS, OpenStreetMap',
        'Step-by-step instructions for each map',
        'Look up your address to save your coordinates',
        'Save for binder',
      ],
      className: 'map-index',
      status: 'early development',
    },
    {
      id: 'financial-summary',
      title: 'Financial & Insurance Summary',
      description:
        'A safe-to-print overview of your emergency fund, insurance, and debts — no account numbers included.',
      icon: '💰',
      path: '/financial-summary',
      features: [
        'Emergency fund summary',
        'Insurance (type + company)',
        'Debt overview',
        'Save',
      ],
      className: 'financial-summary',
      status: 'early development',
    },
    {
      id: 'valuables',
      title: 'Valuables Inventory',
      description:
        'List your valuable items so you have a record for insurance if something happens. Save for your binder.',
      icon: '🏆',
      path: '/valuables',
      features: [
        'Item name, category, value',
        'Location or photo notes',
        'Save',
      ],
      className: 'valuables',
      status: 'early development',
    },
    {
      id: 'babysitter-reference',
      title: 'Babysitter Quick Reference',
      description:
        'Make a one-page sheet with everything a babysitter needs — emergency numbers, your address, medical info, and house rules.',
      icon: '👶',
      path: '/babysitter-reference',
      features: [
        'Emergency numbers and address',
        'Parent and contact info',
        'Print-ready reference card',
      ],
      className: 'babysitter-reference',
      status: 'early development',
    },
    {
      id: 'binder-hub',
      title: 'Build your binder',
      description:
        'Upload your saved files from each tool, put them in the order you want, and build one printable binder with a cover and table of contents.',
      icon: '📚',
      path: '/binder-hub',
      features: [
        'Upload saved files from any tool',
        'Reorder, remove, or rename sections',
        'Build one printable binder with a cover and table of contents',
        'Binder care tips',
      ],
      className: 'binder-hub',
      status: 'early development',
    },
  ];

  return (
    <div className="hub-container">
      <a
        href="https://wahletec.com"
        className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-primary transition-colors mb-4"
      >
        <ArrowLeft size={16} />
        wahletec.com
      </a>
      <div className="hub-header fade-in">
        <h1 className="hub-title">🛠️ Preparedness Tool Hub</h1>
        <p className="hub-subtitle">
          All your home and emergency planning tools in one place
        </p>
      </div>

      {/* Privacy & Getting Started Section */}
      <div className="fade-in" style={{ animationDelay: '0.1s' }}>
        <div className="max-w-4xl mx-auto mb-8 space-y-4 px-4">
          {/* Privacy Notice */}
          <div className="rounded-xl border border-theme-accent/30 bg-theme-accent/5 p-5">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-theme-accent flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-text-primary mb-1">Your privacy is built in</h3>
                <p className="text-sm text-text-muted leading-relaxed">
                  Your data stays on your device — it never gets sent to us. We don't collect or store
                  any of your information. This keeps your personal details private and safe.
                </p>
              </div>
            </div>
          </div>

          {/* Save/Load Notice */}
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
            <div className="flex items-start gap-3">
              <HardDrive className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-text-primary mb-1">Save your work</h3>
                <p className="text-sm text-text-muted leading-relaxed">
                  Your data is stored in this browser only. If you clear your browser data or use a different
                  device, it will be gone. Use the <strong>Save</strong> button in each tool to download a
                  backup. Use <strong>Load</strong> to bring it back.
                </p>
              </div>
            </div>
          </div>

          {/* Clear Data + Getting Started Buttons */}
          <div className="flex justify-center gap-3 flex-wrap">
            <Dialog onOpenChange={(open) => { if (open) setPulsing(false); }}>
              <DialogTrigger asChild>
                <button
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-theme-accent/20 text-theme-accent hover:bg-theme-accent/30 font-medium transition-colors text-base ${pulsing ? 'animate-pulse' : ''}`}
                >
                  <BookOpen className="w-5 h-5" />
                  Getting Started
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl">Getting Started</DialogTitle>
                  <DialogDescription>
                    Here's how to get started.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <ol className="list-decimal list-inside space-y-3 text-sm text-text-primary">
                    <li className="leading-relaxed">
                      <span className="font-medium">Look around</span> — Browse the cards below and open any tool. Each one helps with a different part of being prepared.
                    </li>
                    <li className="leading-relaxed">
                      <span className="font-medium">Fill in your data</span> — Work through each tool at your own pace. Your data is saved automatically in your browser.
                    </li>
                    <li className="leading-relaxed">
                      <span className="font-medium">Save your work</span> — Use the Save button in each tool to download your data as a file. This is your backup — keep it safe.
                    </li>
                    <li className="leading-relaxed">
                      <span className="font-medium">Build your binder</span> — When you're ready, go to "Build your binder" to put all your saved files together into one printable document.
                    </li>
                  </ol>

                  {/* Video placeholder */}
                  <div className="aspect-video rounded-lg border-2 border-dashed border-gray-600 flex flex-col items-center justify-center text-text-muted">
                    <PlayCircle className="w-12 h-12 mb-2 opacity-40" />
                    <p className="text-sm font-medium">Hub overview video coming soon</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 font-medium transition-colors text-base"
                >
                  <Trash2 className="w-5 h-5" />
                  Clear All My Data
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will delete all of your data in this browser — inventory, documents, emergency
                    plans, maps, and everything else. You can't undo this. Make sure you've saved anything
                    you want to keep first.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => {
                      APP_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
                      window.location.reload();
                    }}
                  >
                    Yes, delete everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div className="tools-grid">
        {tools.slice(0, 4).map((tool, index) => (
          <Link
            key={tool.id}
            to={tool.path}
            className="tool-card fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="tool-header flex items-start gap-4 mb-4">
              <div className="tool-icon text-3xl" aria-hidden>
                {tool.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="tool-title text-xl font-bold">{tool.title}</div>
                <div className="mt-2">
                  <span
                    className={`tool-status inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                      tool.status === 'testing'
                        ? 'bg-amber-500/20 text-amber-400'
                        : tool.status === 'early development'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-theme-accent/20 text-theme-accent'
                    }`}
                  >
                    {tool.status === 'testing' && '🧪 Testing'}
                    {tool.status === 'early development' && (
                      <>
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        Early development
                      </>
                    )}
                    {tool.status !== 'testing' && tool.status !== 'early development' && '✓ Ready'}
                  </span>
                </div>
              </div>
            </div>
            <div className="tool-description mb-5 leading-relaxed">{tool.description}</div>
            <ul className="tool-features list-none mb-5 space-y-2">
              {tool.features.map((feature, i) => (
                <li key={i} className="pl-5 relative">
                  <span className="absolute left-0 text-theme-accent font-semibold">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            <div className="tool-cta flex items-center gap-2 font-medium">
              Open Tool <ExternalLink size={16} />
            </div>
          </Link>
        ))}
      </div>

      <div className="risk-cta-banner">
        <div className="flex items-center gap-4 flex-wrap">
          <BarChart3 className="w-8 h-8 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-bold text-text-primary text-lg mb-1">Know Your Risks Before You Plan</h3>
            <p className="text-sm text-text-muted leading-relaxed">
              Find out which emergencies are most likely where you live — so you know what to prepare for first.
            </p>
          </div>
          <a href="#" target="_blank" rel="noopener noreferrer"
             className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 shadow-sm flex items-center gap-2 whitespace-nowrap">
            Get Your Risk Report <ExternalLink size={16} />
          </a>
        </div>
      </div>

      <div className="tools-grid">
        {tools.slice(4).map((tool, index) => (
          <Link
            key={tool.id}
            to={tool.path}
            className="tool-card fade-in"
            style={{ animationDelay: `${(index + 4) * 0.1}s` }}
          >
            <div className="tool-header flex items-start gap-4 mb-4">
              <div className="tool-icon text-3xl" aria-hidden>
                {tool.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="tool-title text-xl font-bold">{tool.title}</div>
                <div className="mt-2">
                  <span
                    className={`tool-status inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                      tool.status === 'testing'
                        ? 'bg-amber-500/20 text-amber-400'
                        : tool.status === 'early development'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-theme-accent/20 text-theme-accent'
                    }`}
                  >
                    {tool.status === 'testing' && '🧪 Testing'}
                    {tool.status === 'early development' && (
                      <>
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        Early development
                      </>
                    )}
                    {tool.status !== 'testing' && tool.status !== 'early development' && '✓ Ready'}
                  </span>
                </div>
              </div>
            </div>
            <div className="tool-description mb-5 leading-relaxed">{tool.description}</div>
            <ul className="tool-features list-none mb-5 space-y-2">
              {tool.features.map((feature, i) => (
                <li key={i} className="pl-5 relative">
                  <span className="absolute left-0 text-theme-accent font-semibold">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            <div className="tool-cta flex items-center gap-2 font-medium">
              Open Tool <ExternalLink size={16} />
            </div>
          </Link>
        ))}
      </div>

      <div className="hub-footer">
        <p>Professional preparedness tools by Wahle-Tec</p>
      </div>
    </div>
  );
};

export default ToolHub;
